// Service-worker-side article write-back for the offline download phase
// (design §8.5). SW-safe: pure Dexie (`db.articles`) + response parsing only —
// NO apiClient, NO localStorage. The same `writeArticleBatch` writer is shared
// with the foreground fallback so both platform paths produce identical rows.

import db from '../db';
import { contentHash } from '../db/contentHash';

// Background Fetch registrations this app owns are tagged with this prefix so
// the SW ignores any unrelated background fetches.
export const BG_FETCH_TAG_PREFIX = 'rin-articles-';

/**
 * Upsert a list of full article payloads into `db.articles`. Idempotent and
 * safe to re-apply (content-hash short-circuit), mirroring Phase 3's
 * recovery-safety posture: a late Background Fetch write-back may overlap the
 * foreground reconciler, but both are idempotent reads so overlap is at worst
 * wasteful, never corrupting.
 *
 * Merge semantics: full content replaces any prior row (upgrading a list-row
 * write-through to full content, §7.1) while the user-local `last_opened_at`
 * is preserved from the existing row.
 *
 * @param {Array<object>} articles - Full article payloads (each with an `id`).
 * @returns {Promise<number>} Count of rows whose content actually changed.
 */
export async function writeArticleBatch(articles) {
  if (!Array.isArray(articles) || articles.length === 0) return 0;
  const now = Date.now();

  const ids = articles.map(a => a?.id).filter(id => id != null);
  const existingRows = await db.articles.bulkGet(ids);
  const existingById = new Map();
  ids.forEach((id, i) => {
    if (existingRows[i]) existingById.set(id, existingRows[i]);
  });

  const rows = [];
  let changed = 0;
  for (const payload of articles) {
    const id = payload?.id;
    if (id == null) continue;
    const prev = existingById.get(id);
    const hash = await contentHash(payload);
    if (prev && prev.content_hash === hash) {
      // Identical content already cached — refresh recency only (304 posture).
      rows.push({ ...prev, fetched_at: now });
      continue;
    }
    rows.push({
      id,
      payload,
      // Reason: the batch endpoint's ETag covers the whole batch set, not a
      // single article, so there is no per-article ETag to store here. The
      // per-item foreground reconciler (getArticleConditional) re-establishes a
      // per-article ETag on its next conditional GET.
      etag: null,
      fetched_at: now,
      content_hash: hash,
      // Reason: last_opened_at and is_read are client/mutation-owned. A Background
      // Fetch payload is baked at registration and lands LATER, so it may be
      // stale; preserve these on an existing row so a late write-back can't
      // resurrect unread state after the foreground marked the article read. The
      // LIVE per-item reconciler (fetchAndStoreArticle) DOES overwrite is_read
      // because its read is current; this writer is potentially stale.
      last_opened_at: prev?.last_opened_at ?? null,
      is_read: prev ? prev.is_read : (payload.is_read ? 1 : 0),
    });
    changed++;
  }
  await db.articles.bulkPut(rows);
  return changed;
}

/**
 * `backgroundfetchsuccess` handler logic. Reads every matched batch response,
 * parses the article array, and writes it via the shared idempotent writer.
 *
 * @param {BackgroundFetchRegistration} registration - event.registration.
 */
export async function handleBackgroundFetchSuccess(registration) {
  if (!registration || !registration.matchAll) return;
  if (typeof registration.id === 'string' && !registration.id.startsWith(BG_FETCH_TAG_PREFIX)) {
    return;
  }
  let records;
  try {
    records = await registration.matchAll();
  } catch {
    return;
  }
  for (const record of records || []) {
    let response;
    try {
      response = await record.responseReady;
    } catch {
      continue;
    }
    if (!response || !response.ok) continue;
    let articles;
    try {
      articles = await response.json();
    } catch {
      continue;
    }
    if (Array.isArray(articles)) {
      await writeArticleBatch(articles);
    }
  }
}

/**
 * `backgroundfetchfail` / `backgroundfetchabort` handler logic. Deliberate
 * no-op write-side: it does NOT mark the run complete and does NOT write
 * partial content, so any un-fetched articles stay missing and are refilled by
 * the foreground reconciler on next app open (design §8.5). Kept as a named
 * function so the reconcilable-on-failure contract is explicit and testable.
 */
export async function handleBackgroundFetchFailure() {
  // Reason: failure/abort leaves the run reconcilable. The foreground prefetch
  // re-filters against `db.articles` and re-issues only the still-missing
  // batches with full Phase-3 401 handling — so nothing is silently completed.
  return;
}

/**
 * `backgroundfetchclick` handler logic. Focus an existing client or open one.
 */
export async function handleBackgroundFetchClick() {
  if (typeof self === 'undefined' || !self.clients) return;
  const all = await self.clients.matchAll({ type: 'window', includeUncontrolled: true });
  if (all && all.length > 0) {
    try { await all[0].focus(); } catch { /* client gone */ }
    return;
  }
  if (self.clients.openWindow) {
    await self.clients.openWindow('/');
  }
}
